// One step in the sequence builder (GRO-287): type, delay, copy, and "Draft with Scout".
import type { SequenceStep, SequenceStepType } from '../api/types';
import {
  STEP_TYPES, copyLabel, stepHasCopy, stepIsDraftable, stepLabel,
} from '../lib/sequenceBuilder';
import type { StepEdit } from '../lib/sequenceBuilder';

export function SequenceStepCard({
  step, position, day, isFirst, isLast, locked, paused, emailModeSupported, drafting, draftNote, canDraft,
  onEdit, onMove, onRemove, onDraft,
}: {
  step: SequenceStep;
  position: number;
  day: number;
  isFirst: boolean;
  isLast: boolean;
  locked: boolean;
  // A create or launch is out: the inputs are disabled until it returns, so no edit is lost.
  paused: boolean;
  emailModeSupported: boolean;
  drafting: boolean;
  draftNote: string | null;
  canDraft: boolean;
  onEdit: (edit: StepEdit) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  onDraft: () => void;
}) {
  const inputId = `seqstep-${step.step_id}`;

  return (
    <div className="seqstep">
      <div className="seqstephead">
        <span className="seqstepnum">{position}</span>
        {locked ? (
          <span className="seqsteptype">{stepLabel(step.type)}</span>
        ) : (
          <select aria-label={`Step ${position} type`} value={step.type} disabled={paused}
            onChange={event => onEdit({ type: event.target.value as SequenceStepType })}>
            {STEP_TYPES.map(option => <option key={option.type} value={option.type}>{option.label}</option>)}
          </select>
        )}
        <span className="seqday">day {day}</span>
        {locked ? null : (
          <span className="seqstepactions">
            <button type="button" className="seqicon" aria-label={`Move step ${position} up`}
              disabled={isFirst || paused} onClick={() => onMove(-1)}>↑</button>
            <button type="button" className="seqicon" aria-label={`Move step ${position} down`}
              disabled={isLast || paused} onClick={() => onMove(1)}>↓</button>
            <button type="button" className="seqicon" aria-label={`Remove step ${position}`}
              disabled={paused} onClick={onRemove}>×</button>
          </span>
        )}
      </div>

      <div className="seqsteprow">
        <label htmlFor={`${inputId}-delay`}>Wait</label>
        <input id={`${inputId}-delay`} type="number" min={0} max={60} value={step.delay_days} disabled={locked || paused}
          onChange={event => onEdit({ delay_days: Number(event.target.value) })} />
        <span className="seqhint">{isFirst ? 'days after enrolment' : 'days after the previous step'}</span>
      </div>

      {step.type === 'email' && emailModeSupported ? (
        <div className="seqsteprow seqmode" role="radiogroup" aria-label={`Step ${position} sending`}>
          {(['automatic', 'manual'] as const).map(mode => (
            <label key={mode}>
              <input type="radio" name={`${inputId}-mode`} checked={step.email_mode === mode} disabled={locked || paused}
                onChange={() => onEdit({ email_mode: mode })} />
              {mode === 'automatic' ? 'Send automatically' : 'Review before sending'}
            </label>
          ))}
        </div>
      ) : null}

      {step.type === 'email' ? (
        <input className="seqsubject" aria-label={`Step ${position} subject`} placeholder="Subject"
          value={step.subject} disabled={locked || paused} maxLength={200}
          onChange={event => onEdit({ subject: event.target.value })} />
      ) : null}

      {stepHasCopy(step.type) ? (
        <textarea className="seqbody" aria-label={`Step ${position} ${copyLabel(step.type).toLowerCase()}`}
          placeholder={copyLabel(step.type)} value={step.body} disabled={locked || paused} maxLength={5000}
          rows={step.type === 'email' ? 7 : 4}
          onChange={event => onEdit({ body: event.target.value })} />
      ) : null}

      {!locked && stepIsDraftable(step.type) ? (
        <div className="seqsteprow">
          <button type="button" className="pushchip" disabled={drafting || !canDraft} onClick={onDraft}>
            {drafting ? 'Drafting…' : 'Draft with Scout'}
          </button>
          {draftNote ? <span className="seqhint">{draftNote}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
